// SAB-1058: the trade deadline, formatted for display.
//
// This module contains NO rule. The WEEK is stored on the league and the MOMENT is derived
// server-side by nfl_trade_deadline() from the actual schedule -- because kickoff times
// flex, and a client that recomputed "the first game of week N+1" would be a second
// derivation free to disagree with the one the refusal uses. That is the SAB-1035 mistake
// and the SAB-1045 one; this formats what the server says and nothing more.

export interface TradeDeadline {
  enabled: boolean;
  week: number | null;
  deadline_at: string | null;
  has_passed: boolean;
}

export const DEADLINE_WEEK_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 15] as const;
export const DEFAULT_DEADLINE_WEEK = 12;

export function parseTradeDeadline(raw: unknown): TradeDeadline {
  const o = (raw ?? {}) as Record<string, unknown>;
  const enabled = typeof o.enabled === 'boolean' ? o.enabled : true;
  const week = typeof o.week === 'number' ? o.week : null;
  return {
    enabled,
    week: enabled ? (week ?? DEFAULT_DEADLINE_WEEK) : null,
    deadline_at: typeof o.deadline_at === 'string' ? o.deadline_at : null,
    has_passed: o.has_passed === true,
  };
}

/**
 * "Trade deadline: Week 12 (Dec 3)" — the SAB-1037 standard, week AND date.
 *
 * The week alone is ambiguous to anyone not counting; the date alone hides the rule. A
 * deadline with no derivable date says so rather than inventing one: a schedule that has
 * not loaded week N+1 yet genuinely does not know when trading closes.
 */
export function tradeDeadlineLabel(d: TradeDeadline): string {
  if (!d.enabled) return 'No trade deadline';
  const wk = `Week ${d.week ?? DEFAULT_DEADLINE_WEEK}`;
  if (!d.deadline_at) return `Trade deadline: end of ${wk}`;
  const when = new Date(d.deadline_at);
  if (Number.isNaN(when.getTime())) return `Trade deadline: end of ${wk}`;
  const date = when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return d.has_passed
    ? `Trade deadline passed — end of ${wk} (${date})`
    : `Trade deadline: end of ${wk} (${date})`;
}

/** The disclosure line for the contest rules page. Full sentence, same derivation. */
export function tradeDeadlineRule(d: TradeDeadline): string {
  if (!d.enabled) return 'This league has no trade deadline — trades stay open all season.';
  const wk = d.week ?? DEFAULT_DEADLINE_WEEK;
  const date = d.deadline_at
    ? new Date(d.deadline_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;
  return date
    ? `Trades close at the end of Week ${wk}, before the first game of Week ${wk + 1} (${date}). `
      + 'Proposals cannot be made or accepted after that point, and any proposal still open expires unprocessed.'
    : `Trades close at the end of Week ${wk}. Proposals cannot be made or accepted after that point, `
      + 'and any proposal still open expires unprocessed.';
}
